import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaTrophy, FaMedal, FaRunning, FaDumbbell, FaChartLine, FaHeart } from 'react-icons/fa';
import './Fitness.css';

interface Achievement {
  id: string;
  title: string;
  date: string;
  description: string;
  image: string;
  category: string;
  icon: JSX.Element;
}

// Fitness achievements data
const fitnessAchievements: Achievement[] = [
  {
    id: "canada-pro",
    title: "2023 Natural Canada Pro Qualifier Winner",
    date: "2023",
    description: "Achieved first place and overall champion status in the Men's Classic Physique category at the Natural Canada Pro Qualifier, demonstrating exceptional physique development, symmetry, and stage presence.",
    image: "https://picsum.photos/seed/canadapro/800/450",
    category: "Competition",
    icon: <FaTrophy />
  },
  {
    id: "vancouver-overall",
    title: "Vancouver Natural Overall Winner",
    date: "2023",
    description: "Claimed the prestigious overall title in the Men's Classic Physique division at the Vancouver Natural competition, showcasing superior conditioning, muscle development, and posing routine.",
    image: "https://picsum.photos/seed/vancouver/800/450",
    category: "Championship",
    icon: <FaTrophy />
  },
  {
    id: "classic-gold",
    title: "3x Gold Medalist: Natural Men's Classic Physique",
    date: "2021-2023",
    description: "Secured gold medal three times in the Natural Men's Classic Physique category, consistently demonstrating superior physical development, symmetry, and stage presence across multiple competitions.",
    image: "https://picsum.photos/seed/classicgold/800/450",
    category: "Medal",
    icon: <FaMedal />
  },
  {
    id: "physique-gold",
    title: "3x Gold Medalist: Natural Men's Physique",
    date: "2021-2022",
    description: "Earned three gold medals in the Natural Men's Physique division, showcasing exceptional V-taper, conditioning, and overall aesthetic development against top-tier competitors.",
    image: "https://picsum.photos/seed/physiquegold/800/450",
    category: "Medal",
    icon: <FaMedal />
  },
  {
    id: "pro-winner",
    title: "2023 Natural Pro Winner: Men's Classic Physique",
    date: "2023",
    description: "Secured victory at the professional level in the Men's Classic Physique division, demonstrating world-class muscular development, symmetry, and posing execution among elite competitors.",
    image: "https://picsum.photos/seed/prowin/800/450",
    category: "Professional",
    icon: <FaTrophy />
  }
];

// Journey milestones data
const journeyMilestones = [
  {
    title: "Training Philosophy",
    description: "Combining scientific principles with traditional bodybuilding techniques to maximize natural potential.",
    icon: <FaDumbbell />,
    color: "#e50914"
  },
  {
    title: "Nutrition Strategy",
    description: "Precision nutrition with periodized macronutrient targets supporting both performance and aesthetics.",
    icon: <FaHeart />,
    color: "#0097e6"
  },
  {
    title: "Progress Tracking",
    description: "Data-driven approach to monitor improvements and optimize training variables for continuous growth.",
    icon: <FaChartLine />,
    color: "#44bd32"
  },
  {
    title: "Recovery Methods",
    description: "Comprehensive recovery protocols including sleep optimization, mobility work, and stress management.",
    icon: <FaRunning />,
    color: "#f39c12"
  }
];

const Fitness: React.FC = () => {
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const categories = Array.from(new Set(fitnessAchievements.map(a => a.category)));
  
  const filteredAchievements = activeCategory 
    ? fitnessAchievements.filter(a => a.category === activeCategory)
    : fitnessAchievements;

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const handleAchievementClick = (achievement: Achievement) => {
    setSelectedAchievement(achievement);
  };

  const closeModal = () => {
    setSelectedAchievement(null);
  };

  return (
    <div className="fitness-container">
      <motion.div 
        className="fitness-hero"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="hero-content">
          <h1>Championship Journey</h1>
          <p>From dedication to gold medals - the pursuit of natural excellence</p>
        </div>
      </motion.div>

      <div className="category-filter">
        <button 
          className={activeCategory === null ? 'active' : ''} 
          onClick={() => setActiveCategory(null)}
        >
          All
        </button>
        {categories.map(category => (
          <button 
            key={category}
            className={activeCategory === category ? 'active' : ''}
            onClick={() => setActiveCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      <motion.div 
        className="achievements-grid"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {filteredAchievements.map((achievement) => (
          <motion.div
            key={achievement.id}
            className="achievement-card"
            variants={cardVariants}
            onClick={() => handleAchievementClick(achievement)}
            whileHover={{ scale: 1.03 }}
            transition={{ duration: 0.2 }}
          >
            <div className="achievement-image">
              <img src={achievement.image} alt={achievement.title} />
              <div className="achievement-overlay">
                <div className="achievement-icon">{achievement.icon}</div>
                <div className="achievement-category">{achievement.category}</div>
                <div className="achievement-date">{achievement.date}</div>
              </div>
            </div>
            <h3>{achievement.title}</h3>
          </motion.div>
        ))}
      </motion.div>

      <motion.div 
        className="journey-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.8 }}
      >
        <h2>The Journey</h2>
        <div className="journey-grid">
          {journeyMilestones.map((milestone, index) => (
            <motion.div 
              key={index} 
              className="journey-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 * index, duration: 0.5 }}
            >
              <div className="journey-icon" style={{ backgroundColor: milestone.color }}>
                {milestone.icon}
              </div>
              <h3>{milestone.title}</h3>
              <p>{milestone.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {selectedAchievement && (
        <motion.div 
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeModal}
        >
          <motion.div 
            className="modal-content"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            onClick={e => e.stopPropagation()}
          >
            <button className="modal-close" onClick={closeModal}>×</button>
            <div className="modal-image">
              <img src={selectedAchievement.image} alt={selectedAchievement.title} />
              <div className="modal-badge">
                {selectedAchievement.icon}
                <span>{selectedAchievement.category}</span>
              </div>
            </div>
            <div className="modal-info">
              <h2>{selectedAchievement.title}</h2>
              <p className="modal-date">{selectedAchievement.date}</p>
              <p className="modal-description">{selectedAchievement.description}</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Fitness; 